import React, { useMemo, useState } from "react";
import { Search, Clock, AlertTriangle, CheckCircle2, ChevronRight, FileText } from "lucide-react";
import { LOGBERG_CALLS, severityClasses, shortTime, DEMO_DISCLAIMER } from "../../demo/demoData";
import { useDemo } from "../../demo/DemoContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { toast } from "sonner";

const riskTone = { High: "red", Medium: "amber", Low: "green" };

export default function LogbergDemo({ onGenerate }) {
  const { addFromCall, reviewIncident } = useDemo();
  const [selectedId, setSelectedId] = useState(LOGBERG_CALLS[2].id);
  const [query, setQuery] = useState("");
  const [riskFilter, setRiskFilter] = useState("all");
  const [note, setNote] = useState("");
  const [decision, setDecision] = useState(null);
  const [evidenceId, setEvidenceId] = useState(null);

  const calls = useMemo(() => {
    return LOGBERG_CALLS.filter((c) => {
      const q = query.trim().toLowerCase();
      const matchQ = !q || c.id.toLowerCase().includes(q) || c.company.toLowerCase().includes(q) || c.categories.join(" ").toLowerCase().includes(q);
      const matchR = riskFilter === "all" || c.risk === riskFilter;
      return matchQ && matchR;
    });
  }, [query, riskFilter]);

  const call = LOGBERG_CALLS.find((c) => c.id === selectedId);

  const applyDecision = (d) => {
    setDecision(d);
    const primaryFlag = call.flags[0] || null;
    const incident = addFromCall(call, primaryFlag);
    setEvidenceId(incident.id);
    reviewIncident(incident.id, d === "Confirm issue" ? "Confirm issue" : d, note);
    toast.success(`Decision recorded: ${d} (${incident.id})`);
  };

  return (
    <div className="rounded-lg border border-white/10 bg-navy-900/60 overflow-hidden">
      <div className="grid lg:grid-cols-[340px_1fr]">
        {/* Call list */}
        <div className="border-b lg:border-b-0 lg:border-r border-white/10">
          <div className="p-4 border-b border-white/10 space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search calls\u2026" className="pl-9 bg-navy-800 border-white/12" />
            </div>
            <Select value={riskFilter} onValueChange={setRiskFilter}>
              <SelectTrigger className="bg-navy-800 border-white/12"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-navy-800 border-white/12">
                <SelectItem value="all">All risk levels</SelectItem>
                <SelectItem value="High">High</SelectItem>
                <SelectItem value="Medium">Medium</SelectItem>
                <SelectItem value="Low">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="max-h-[520px] overflow-y-auto">
            {calls.length === 0 && (
              <div className="p-8 text-center text-sm text-muted-foreground">No calls match your filters.</div>
            )}
            {calls.map((c) => (
              <button
                key={c.id}
                onClick={() => { setSelectedId(c.id); setDecision(null); setEvidenceId(null); setNote(""); }}
                className={`w-full text-left px-4 py-3.5 border-b border-white/5 transition-colors ${selectedId === c.id ? "bg-primary/[0.08]" : "hover:bg-white/[0.03]"}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-mono text-foreground">{c.id}</span>
                  <span className={`rounded-full border px-2 py-0.5 text-[0.7rem] font-semibold ${severityClasses[riskTone[c.risk]]}`}>{c.risk}</span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground truncate">{c.company}</p>
                <div className="mt-1.5 flex items-center gap-3 text-[0.7rem] text-muted-foreground">
                  <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {c.duration}</span>
                  <span>{shortTime(c.date)}</span>
                  <span>{c.status}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Detail */}
        <div className="p-5 sm:p-6">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <p className="font-heading text-lg font-semibold text-foreground">{call.id}</p>
              <p className="text-sm text-muted-foreground">{call.company}</p>
            </div>
            <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${severityClasses[riskTone[call.risk]]}`}>{call.risk} risk</span>
          </div>
          <p className="mt-3 text-sm text-muted-foreground">{call.summary}</p>

          {/* Transcript */}
          <div className="mt-5 rounded-xl border border-white/10 bg-white/[0.02] p-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" /> Transcript</p>
            <div className="mt-3 space-y-2.5">
              {call.transcript.map((line, i) => (
                <div key={i} className={`rounded-lg px-3 py-2 ${line.flag ? "bg-signal-amber/10 border border-signal-amber/25" : ""}`}>
                  <div className="flex items-center gap-2 text-[0.7rem] text-muted-foreground">
                    <span className="font-mono">{line.t}</span>
                    <span className="font-medium text-foreground/80">{line.speaker}</span>
                    {line.flag && <span className="inline-flex items-center gap-1 text-signal-amber"><AlertTriangle className="h-3 w-3" /> flagged</span>}
                  </div>
                  <p className="mt-1 text-sm text-foreground">{line.text}</p>
                  {line.flag && <p className="mt-1 text-xs text-signal-amber">{line.trigger}</p>}
                </div>
              ))}
            </div>
          </div>

          {/* Flags */}
          {call.flags.length > 0 && (
            <div className="mt-4 space-y-2.5">
              {call.flags.map((f) => (
                <div key={f.category} className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-foreground">{f.category}</span>
                    <span className="text-xs text-muted-foreground">Confidence {Math.round(f.confidence * 100)}%</span>
                  </div>
                  <p className="mt-1.5 text-sm text-muted-foreground">{f.why}</p>
                  <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
                    <span>Policy: <span className="text-foreground">{f.policy}</span></span>
                    <span>Suggested action: <span className="text-signal-amber">{f.suggested}</span></span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Reviewer panel */}
          <div className="mt-5 rounded-xl border border-primary/25 bg-primary/[0.05] p-4">
            <p className="text-sm font-semibold text-foreground">Reviewer decision \u00b7 FCA-aligned, reviewer-controlled</p>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Add a reviewer note (optional)" className="mt-3 bg-navy-800 border-white/12 resize-none" />
            <div className="mt-3 flex flex-wrap gap-2">
              {["Confirm issue", "False positive", "Escalate", "Request further information", "Mark reviewed"].map((d) => (
                <Button key={d} size="sm" variant="outline" onClick={() => applyDecision(d)} className={`border-white/15 bg-transparent hover:bg-white/5 text-foreground ${decision === d ? "ring-1 ring-primary" : ""}`}>
                  {d === "Confirm issue" ? <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> : null}{d}
                </Button>
              ))}
            </div>
            {decision && (
              <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-white/10 pt-3">
                <span className="text-sm text-muted-foreground">Decision recorded. Evidence captured as {evidenceId}.</span>
                <Button size="sm" className="ml-auto bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => onGenerate && onGenerate(evidenceId)}>
                  Generate evidence <ChevronRight className="ml-0.5 h-3.5 w-3.5" />
                </Button>
              </div>
            )}
          </div>

          <p className="mt-5 text-xs text-muted-foreground/80">{DEMO_DISCLAIMER}</p>
        </div>
      </div>
    </div>
  );
}
